import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react'
import * as React from 'react'

import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useMediaQuery } from 'usehooks-ts'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
	return (
		<nav
			role="navigation"
			aria-label="pagination"
			data-slot="pagination"
			className={cn('mx-auto flex-1 flex w-full justify-center', className)}
			{...props}
		/>
	)
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot="pagination-content"
			className={cn('flex flex-row items-center gap-2', className)}
			{...props}
		/>
	)
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
	return <li data-slot="pagination-item " {...props} />
}

type PaginationLinkProps = {
	isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
	React.ComponentProps<'a'>

function PaginationLink({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) {
	return (
		<a
			aria-current={isActive ? 'page' : undefined}
			data-slot="pagination-link"
			data-active={isActive}
			className={cn(
				'border rounded-lg py-1.5 px-3 text-xs lg:text-sm font-medium',
				isActive
					? '!bg-[#0B224D0D] !border-secondary text-secondary'
					: 'border-[#EEEFF0] text-muted-foreground',
				buttonVariants({
					variant: isActive ? 'outline' : 'ghost',
					size,
				}),
				className,
			)}
			{...props}
		/>
	)
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label="Go to previous page"
			size="default"
			className={cn('border-0 text-secondary gap-1 px-2.5 sm:pl-2.5', className)}
			{...props}>
			<ChevronLeftIcon />
			<span className="hidden">Previous</span>
		</PaginationLink>
	)
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label="Go to next page"
			size="default"
			className={cn('border-0 text-secondary gap-1 px-2.5 sm:pr-2.5', className)}
			{...props}>
			<span className="hidden">Next</span>
			<ChevronRightIcon />
		</PaginationLink>
	)
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
	return (
		<span
			aria-hidden
			data-slot="pagination-ellipsis"
			className={cn('flex size-9 text-muted-foreground items-center justify-center', className)}
			{...props}>
			<MoreHorizontalIcon className="size-4" />
			<span className="sr-only">More pages</span>
		</span>
	)
}

interface PaginationProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
}

function PaginationComponent({ currentPage, totalPages, onPageChange }: PaginationProps) {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const getPages = () => {
		const pages: (number | 'ellipsis')[] = []

		if (totalPages <= (isSmallScreen ? 2 : 3)) {
			for (let i = 1; i <= totalPages; i++) pages.push(i)
		} else {
			pages.push(1)

			if (currentPage > (isSmallScreen ? 1 : 2)) pages.push('ellipsis')
			const range = isSmallScreen ? 0 : 1
			const start = Math.max(2, currentPage - range)
			const end = Math.min(totalPages - 1, currentPage + range)

			for (let i = start; i <= end; i++) pages.push(i)

			if (currentPage < totalPages - (isSmallScreen ? 0 : 1)) pages.push('ellipsis')

			pages.push(totalPages)
		}

		return pages
	}

	const pages = getPages()

	return (
		<Pagination>
			<PaginationContent>
				<PaginationItem>
					<PaginationPrevious href="#" onClick={() => onPageChange(Math.max(1, currentPage - 1))} />
				</PaginationItem>

				{pages.map((page, index) =>
					page === 'ellipsis' ? (
						<PaginationItem key={`ellipsis-${index}`}>
							<PaginationEllipsis />
						</PaginationItem>
					) : (
						<PaginationItem key={page}>
							<PaginationLink
								href="#"
								isActive={currentPage === page}
								onClick={() => onPageChange(page as number)}>
								{page}
							</PaginationLink>
						</PaginationItem>
					),
				)}

				<PaginationItem>
					<PaginationNext
						href="#"
						onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
					/>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	)
}

export {
	Pagination,
	PaginationComponent,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
}
